/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from 'react';
import styles, { filterTableWidth } from './styles';
import { Box, Checkbox, Drawer, Fab, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { ArrowDropUp, CancelPresentationRounded, ChevronLeft, ChevronRight, Circle, Close, FilterAltRounded, KeyboardArrowUp, LinearScaleOutlined, RestoreOutlined, Square } from '@mui/icons-material';
import columns from './table_config';
import { FILTER_STYLE_PARAMETRIC_ANALYSIS, MAIN_DEPTH_POINTS, PARAMETER_ANALYSIS_FILTER_PARAMS, PARAMETRIC_ANALYSIS_FILTER_LABEL } from '../../../../utils/constants';
import CanvasJSReact from '../../../ui/canvasjs/canvasjs.react';
import { HYDRAULICS_CHART_COLORS } from '../../../../shared/config/hydraulics_constants';
import { getMinMaxOfKey } from '../../../../utils/objectHelpers';
import { COLORS } from '../../../../shared/config/colors';
import { getOptimalInterval } from '../../../../utils/getOptimalInterval';
import Datagrid from '../../../ui/Datagrid';
import ChartsAxisPopover from '../../../ui/ChartsAxisPopover';
import { isMobile } from 'react-device-detect';
import RunDetailsChartDialog from './RunDetailsChart';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const ParametricAnalysisChart = ({
  data: parametricAnalysisChartData,
  units,
  window,
  hydraulicsLanding
}) => {
  const classes = styles();
  const [tableRow, setTableRows] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [plotFor, setPlotFor] = useState('ECD');
  const [chartData, setChartData] = useState([]);
  const [showRunChart, setShowRunChart] = useState({
    open: false,
    data: null
  });
  const containerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const [showFilter, setShowFilter] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [hiddenChartData, setHiddenChartData] = useState([]);
  const [minMax_X, setMinMax_X] = useState({});
  const [minMax_Y, setMinMax_Y] = useState({});
  const [defaultXAxis, setDefaultXAxis] = useState({});
  const [defaultYAxis, setDefaultYAxis] = useState({});
  const [customColumn, setCustomColumn] = useState({
    header: null,
    field: null,
    show: true
  });
  const [chartXAxis, setChartXAxis] = useState({
    min: '',
    max: ''
  });
  const [chartYAxis, setChartYAxis] = useState({
    min: '',
    max: ''
  });
  const [showMobileChartTable, setShowMobileChartTable] = useState(false);
  const parametricUnits = units?.parametricAnalysis;

  const handleScroll = () => {
    if (containerRef.current) {
      scrollPositionRef.current = containerRef.current.scrollTop;
    }
  };

  const [chartConfig, setChartConfig] = useState([
    {
      name: 'PP',
      key: 'pp',
      color: HYDRAULICS_CHART_COLORS.PORE_PRESSURE,
      hide: false,
      tooltip: `MD: {y} ${window?.units?.md?.label} <br/> PP: {x} ${window?.units?.pp?.label}`
    },
    {
      name: 'FP',
      key: 'fp',
      color: HYDRAULICS_CHART_COLORS.FRACTURE_PRESSURE,
      hide: false,
      tooltip: `MD: {y} ${window?.units?.md?.label} <br/> FP: {x} ${window?.units?.fp?.label}`
    },
  ]);

  useEffect(() => {
    if (!parametricAnalysisChartData?.inputs?.length) return;

    const inputs = parametricAnalysisChartData.inputs;
    const maxLength = Math.max(...inputs.map(({ values }) => values.length));
    const transformed = Array.from({ length: maxLength }, (_, i) => {
      const getFieldName = (index) => {
        const names = ['First', 'Second', 'Third', 'Fourth'];
        return names[index] ? `${names[index]} Value` : `Value#${index + 1}`;
      };

      const row = {
        _id: Math.random().toString(36).substring(2, 10),
        label: `Value#${i + 1}`,
        labelName: getFieldName(i),
        isChecked: {}
      };
      inputs.forEach(({ parameter, values }) => {
        row[parameter] = values[i];
        row.isChecked[parameter] = true;
      });
      return row;
    });
    setFilterData(transformed);
  }, [parametricAnalysisChartData]);

  useEffect(() => {
    setShowFilter(!!parametricAnalysisChartData.results);
    setShowTable(!!parametricAnalysisChartData.results);
  }, [parametricAnalysisChartData.results]);

  useEffect(() => {
    if (!parametricAnalysisChartData?.results?.length && !filterData?.length) return;
    const targetData = parametricAnalysisChartData?.run_details?.target_data;
    const anchorPoint = targetData?.anchor_point;
    const isTargetDataEmpty = !targetData || Object.keys(targetData).length === 0 || targetData?.anchor_point === 'None';
    const isExcluded = MAIN_DEPTH_POINTS.includes(anchorPoint);
    if (isTargetDataEmpty) {
      setCustomColumn({ header: null, show: true, field: null });
    } else {
      const headerName = anchorPoint.replace(/_/g, ' ').toUpperCase();
      const fieldName = anchorPoint.replace(/ /g, '_')?.toLowerCase();
      setCustomColumn(
        isExcluded
          ? { header: null, show: true, field: null }
          : { header: headerName, show: false, field: `${fieldName}` }
      );
    }
    const plotKey = plotFor === 'ECD' ? 'ecd_with_sbp' : 'esd_with_sbp';
    const plotName = plotFor;
    const unitsDepth = parametricUnits?.results?.depth_points;

    const getStyle = (value, key) => {
      const values = PARAMETRIC_ANALYSIS_FILTER_LABEL.map(label => filterData.find(item => item.label === label)?.[key] ?? 0);
      return PARAMETRIC_ANALYSIS_FILTER_LABEL[values.indexOf(value)];
    };

    const processedData = parametricAnalysisChartData.results.map(({ depth_points = [], depths_summary = {}, inputs = {}, summary = {} }, index) => {
      const styles = parametricAnalysisChartData.inputs.reduce((acc, { parameter }) => {
        acc[parameter] = getStyle(inputs[parameter], parameter);
        return acc;
      }, {});
      return {
        summary,
        depth_points,
        depths_summary,
        inputs,
        hide: false,
        name: plotName,
        key: plotKey,
        index,
        runNumber: index + 1,
        type: 'line',
        color: FILTER_STYLE_PARAMETRIC_ANALYSIS[styles[parametricAnalysisChartData?.inputs[0]?.parameter]]?.color || 'grey',
        markerType: FILTER_STYLE_PARAMETRIC_ANALYSIS[styles[parametricAnalysisChartData?.inputs[1]?.parameter]]?.markerType || '',
        markerSize: isMobile ? 6 : 8,
        lineDashStyle: FILTER_STYLE_PARAMETRIC_ANALYSIS[styles[parametricAnalysisChartData?.inputs[2]?.parameter]]?.lineDashStyle || 'solid',
        lineDashType: FILTER_STYLE_PARAMETRIC_ANALYSIS[styles[parametricAnalysisChartData?.inputs[2]?.parameter]]?.lineDashType || 'solid',
        lineThickness: 1,
        toolTipContent: `Run ${index + 1} <br/> MD: {y} ${unitsDepth?.md?.label} <br/> ${plotName}: {${plotKey}} ${unitsDepth?.esd?.label} <br/>`,
        showInLegend: false,
        dataPoints: depth_points.map(({ md, ...item }) => ({
          x: item[plotKey],
          y: md,
          ...item,
        })),
      };
    });

    const records = processedData.map((record, index) => {
      const { depths_summary = {}, inputs = {}, summary = {} } = record;
      const casingShoe = depths_summary.casing_shoe?.[plotKey] ?? null;
      const landingPoint = depths_summary.landing_point?.[plotKey] ?? null;
      const bottomHole = depths_summary.hole_depth?.[plotKey] ?? null;
      const connectionSbp = summary.connection_sbp ?? null;
      const baseRecord = {
        ...record,
        index,
        hide: true,
        runNumber: index + 1,
        casing_shoe: casingShoe,
        landing_point: landingPoint,
        bottom_hole: bottomHole,
        connection_sbp: connectionSbp,
        _id: Math.random().toString(36).substring(2, 10),
        rop: plotFor === 'ESD' ? `(${inputs?.rop}>>0)` : inputs.rop ?? 0.0,
        rpm: plotFor === 'ESD' ? `(${inputs?.rpm}>>0)` : inputs.rpm ?? 0.0,
        flow_in: plotFor === 'ESD' ? `(${inputs?.flow_in}>>0)` : inputs.flow_in ?? null,
        dynamic_sbp: summary?.target_sbp ?? null,
        mw: inputs.mw ?? null,
        hole_depth: inputs.hole_depth ?? null,
      };

      if (!isTargetDataEmpty && !isExcluded) {
        const fieldName = anchorPoint.replace(/ /g, '_')?.toLowerCase();
        const anchorValue = depths_summary?.[fieldName]?.[plotKey] ?? null;
        baseRecord[fieldName] = anchorValue;
      }

      return baseRecord;
    });

    const isAllFalseForAnyParam = PARAMETER_ANALYSIS_FILTER_PARAMS.some(param => {
      return filterData.every(item => item.isChecked[param] === false);
    });

    if (isAllFalseForAnyParam) {
      setChartData([]);
      setTableRows([]);
      return;
    }

    const conditions = filterData.reduce((acc, { isChecked, ...criteria }) => {
      Object.entries(isChecked).forEach(([key, checked]) => {
        if (checked) {
          acc[key] = acc[key] || new Set();
          acc[key].add(criteria[key]);
        }
      });
      return acc;
    }, {});

    const isMatch = ({ inputs }) => Object.entries(conditions).every(([key, values]) => values.has(inputs[key]));
    const processedChartData = processedData.filter(isMatch);

    const processDepthPoints = processedChartData.map((item) => {
      return item.dataPoints;
    }).flatMap(item => item);
    const axisY = getMinMaxOfKey(processDepthPoints, 'y');
    setMinMax_Y({
      min: axisY.min,
      max: axisY.max,
    });
    setChartData(processedChartData);
    const x_axis_viewPort = generateViewportMinMax(processedChartData);
    if (x_axis_viewPort) {
      setMinMax_X(x_axis_viewPort);
    }
    setHiddenChartData(processedChartData);
    setTableRows(records.filter(isMatch));
  }, [parametricAnalysisChartData, plotFor, filterData]);

  const handleResetAll = () => {
    setFilterData((prevData) =>
      prevData.map((item) => ({
        ...item,
        isChecked: Object.fromEntries(
          Object.keys(item.isChecked).map((key) => [key, true])
        ),
      }))
    );
  };

  const columns_table = [
    ...(parametricAnalysisChartData?.inputs || []).map((col, index) => ({
      field: col.parameter,
      headerName: col.parameter === 'flow_in'
        ? 'Flow Rate'
        : col.parameter === 'hole_depth'
          ? 'Hole Depth'
          : col.parameter.toUpperCase(),
      renderHeader: () => (
        <Box>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
            {col.parameter === 'flow_in'
              ? 'Flow Rate'
              : col.parameter === 'hole_depth'
                ? 'Hole Depth'
                : col.parameter.toUpperCase()}
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
            ({parametricUnits?.inputs?.[col.parameter]?.label || ''})
          </Typography>
        </Box>
      ),
      flex: 1,
      minWidth: 190,
      headerAlign: 'center',
      headerClassName: 'table-header',
      renderCell: (params) => {
        const row = params.row;
        const isChecked = row.isChecked[col.parameter];

        return (
          <Box
            className={classes.checkBoxCell}
            sx={{
              background: isChecked && params.value !== undefined ? COLORS.WHITE : 'default',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '50%',
                pl: 5
              }}
            >
              {params.value !== undefined && (
                <Box
                  sx={{
                    width: 42,
                    display: 'flex',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={row.isChecked[col.parameter]}
                    onChange={() =>
                      handleCheckboxChange(row._id, col.parameter)
                    }
                  />
                </Box>
              )}

              <Box
                className={classes.checkBoxValue}
                sx={{
                  minWidth: 70,
                  textAlign: 'left',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {params?.value?.toFixed(
                  parametricUnits?.inputs?.[col.parameter]?.decimals
                )}
              </Box>
            </Box>

            <Box sx={{ flexShrink: 0, width: '50%', display: 'flex', justifyContent: 'center' }}>
              {index === 1 && params.value !== undefined && (
                <IconButton
                  className={classes.filterTableMarker}
                >
                  {row?.label === 'Value#3' && <ArrowDropUp sx={{ fontSize: { xs: 20, sm: 50 }, color: COLORS.GRAY }} />}
                  {row?.label === 'Value#1' && <Circle sx={{ fontSize: { xs: 10, sm: 15 }, color: COLORS.GRAY }} />}
                  {row?.label === 'Value#2' && <Square sx={{ fontSize: { xs: 10, sm: 15 }, color: COLORS.GRAY }} />}
                  {row?.label === 'Value#4' && <Close sx={{ fontSize: { xs: 10, sm: 25 }, color: COLORS.GRAY }} />}
                </IconButton>
              )}

              {index === 2 && params.value !== undefined && (
                <Box
                  className={classes.lineDashType}
                  sx={
                    row.label === 'Value#4'
                      ? {
                          width: isMobile ? '50px' : '100px',
                          borderTop: isMobile ? '1px solid' : '2px solid',
                          borderImage: `repeating-linear-gradient(
                            to right,
                            grey 0,
                            grey 2px,
                            transparent 2px,
                            transparent 6px,
                            grey 6px,
                            grey 14px,
                            transparent 14px,
                            transparent 18px
                          ) 1`,
                        }
                      : {
                          border: `2px ${FILTER_STYLE_PARAMETRIC_ANALYSIS[row.label]?.lineDashStyle}`,
                        }
                  }
                />
              )}

              {index === 0 && params?.value !== undefined && (
                <Box
                  className={classes.lineColorFlow_in}
                  sx={{
                    border: `2px solid ${FILTER_STYLE_PARAMETRIC_ANALYSIS[row.label]?.color}`,
                  }}
                />
              )}
            </Box>
          </Box>
        );
      },
    })),
    {
      field: 'reset all',
      headerName: 'Reset',
      renderHeader: () => (
        <Box>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
            Reset
          </Typography>
        </Box>
      ),
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (params.row.label === 'Value#1') {
          return (
            <IconButton onClick={() => handleResetAll()}>
              <RestoreOutlined />
            </IconButton>
          );
        }
        return null;
      },
    },
  ];

  const handleCheckboxChange = (rowIndex, field) => {
    setFilterData(prevData =>
      prevData.map((row) =>
        row?._id === rowIndex
          ? { ...row, isChecked: { ...row.isChecked, [field]: !row.isChecked[field] } }
          : row
      )
    );
  };

  useEffect(() => {
    if (minMax_X) setDefaultXAxis(minMax_X);
    if (minMax_Y) setDefaultYAxis(minMax_Y);
  }, [minMax_X, minMax_Y]);

  const generateViewportMinMax = (chartData) => {
    const extractAllDepthPointsArrays = (data) => {
      return data.flatMap(item => item.depth_points);
    };

    const allDepthPoints = extractAllDepthPointsArrays(chartData);
    const minMaxData = [...allDepthPoints, ...window.data];
    const keysToCheck = ['esd_with_sbp', 'ecd_with_sbp', 'pp', 'fp'];

    const allValues = [];

    // Collect all values from the specified keys
    keysToCheck.forEach(key => {
      minMaxData.forEach(obj => {
        if (key in obj) {
          allValues.push(obj[key]);
        }
      });
    });

    // Calculate the overall lowest and highest values
    const overallLowest = Math.min(...allValues);
    const overallHighest = Math.max(...allValues);

    const results = {
      lowest: overallLowest,
      highest: overallHighest,
    };

    return results;
  };

  const generateWindowDataPoints = (config) => {
    const extractAllDepthPointsArrays = (data) => {
      return data.flatMap(item => item.depth_points);
    };

    const allDepthPoints = extractAllDepthPointsArrays(chartData);
    const dataPoints = [];

    if (window?.data.length === 1) {
      const singleDataPoint = window?.data[0];
      dataPoints.push(
        { x: singleDataPoint[config.key], y: 0, markerType: 'none' },
        { x: singleDataPoint[config.key], y: singleDataPoint.md },
        { x: singleDataPoint[config.key], y: allDepthPoints.at(-1).md, markerType: 'none' }
      );
    } else if (window?.data.length > 1) {
      const firstPoint = window?.data[0];
      const lastPoint = window?.data.at(-1);

      if (firstPoint.md > 0) {
        dataPoints.push({ x: firstPoint[config.key], y: 0, markerType: 'none' });
      }

      window?.data.forEach(item => {
        dataPoints.push({ x: item[config.key], y: item.md });
      });

      dataPoints.push({ x: lastPoint[config.key], y: allDepthPoints.at(-1).md, markerType: 'none' });
    }

    const formattedData = dataPoints.map(item => {
      const formattedItem = {
        x: parseFloat(Number(item.x).toFixed(window?.units?.md?.decimals)),
        y: parseFloat(Number(item.y).toFixed(window?.units?.pp?.decimals)),
      };
      if (item.markerType) {
        formattedItem.markerType = item.markerType;
      }
      return formattedItem;
    });
    return formattedData;
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [chartData]);

  const CustomLegend = () => {
    const updateHide = (key, hideValue) => {
      handleScroll();
      setChartConfig(prevItems =>
        prevItems.map(item =>
          item.key === key ? { ...item, hide: hideValue } : item
        )
      );
    };

    return (
         <Grid
          container
          ref={containerRef}
          onScroll={handleScroll}
          height={'40px'}
        >
          {chartConfig.map((item) => (
            <Box
              key={item.name}
              className={classes.legendItem}
              sx={{ opacity: item.hide ? 0.4 : 1 }}
              onClick={() => {
                updateHide(item.key, !item.hide);
              }}
            >
              <LinearScaleOutlined fontSize="large" sx={{ color: item.color, mr: 1 }} />
              {item.name}
            </Box>
          ))}
        </Grid>
    );
  };

  const getValidValue = (val, fallback) =>
    val !== undefined && val !== null && val !== '' ? val : fallback;

  const xMin = getValidValue(chartXAxis?.min, minMax_X?.lowest - 0.1);
  const xMax = getValidValue(chartXAxis?.max, minMax_X?.highest + 0.1);
  const yMin = getValidValue(chartYAxis?.min, defaultYAxis?.min);
  const yMax = getValidValue(chartYAxis?.max, defaultYAxis?.max);

  const options = {
    theme: 'light2',
    animationEnabled: !isMobile,
    zoomEnabled: true,
    exportEnabled: true,
    zoomType: 'y',
    toolbar: {
      itemBackgroundColor: '#d3d3d3',
      itemBackgroundColorOnHover: '#3e3e3e',
      buttonBorderColor: '#3e3e3e',
    },
    backgroundColor: '#FFF',
    axisX: {
      viewportMinimum: xMin,
      viewportMaximum: xMax,
      interval: getOptimalInterval(xMin, xMax, 10),
      valueFormatString: '0.0',
      labelFontSize: isMobile ? 12 : 16,
      gridThickness: 1,
      title: `EMW (${parametricUnits?.results?.depth_points?.esd?.label})`,
      titleFontSize: isMobile ? 14 : 22,
    },
    axisY: {
      viewportMinimum: yMin,
      viewportMaximum: yMax,
      title: `MD (${parametricUnits?.results?.depth_points?.md?.label})`,
      titleFontSize: isMobile ? 14 : 22,
      labelFontSize: isMobile ? 12 : 16,
      includeZero: false,
      valueFormatString: '0',
      reversed: true,
      labelPlacement: 'outside',
      gridThickness: 1,
    },
    height: isMobile ? 520 : 800,
    legend: {
      fontSize: 20,
    },
    data: [
      ...chartConfig.map((config) => {
        if (chartData.length > 0) {
          if (config.hide) return {};
          if (config.key === 'pp' || config.key === 'fp') {
            return {
              type: 'line',
              name: config.name,
              showInLegend: false,
              toolTipContent: config.tooltip,
              dataPoints: generateWindowDataPoints(config),
              color: config.color,
            };
          }
        }
        return {};
      }),
      ...chartData,
    ],
  };

  const handleChartAxisReset = (target) => {
    if (target === 'x' || target === 'resetAll') {
      setChartXAxis({ min: '', max: '' });
    }

    if (target === 'y' || target === 'resetAll') {
      setChartYAxis({ min: '', max: '' });
    }
  };

  const renderRunCell = (params) => {
    const { row } = params;
    const borderStyle = row?.lineDashStyle || 'solid';
    const lineThickness = row?.lineThickness || 2;

    const iconButtonSx =
      borderStyle !== 'dashDot'
        ? {
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              borderTop: `${lineThickness}px ${borderStyle} ${row?.color}`,
              transform: 'translateY(-50%)',
            },
          }
        : {
            width: '100px',
            height: '60px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '2px',
              borderTop: '2px solid',
              borderImage: `repeating-linear-gradient(
                to right,
                ${row?.color} 0,
                ${row?.color} 2px,
                transparent 2px,
                transparent 6px,
                ${row?.color} 6px,
                ${row?.color} 14px,
                transparent 14px,
                transparent 18px
              ) 1`,
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            },
          };

    return (
      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile && 'center' }}>
        <Typography style={{ fontWeight: 800, fontSize: isMobile ? '14px' : '1rem', color: row?.color }}>
          {row?.runNumber}
        </Typography>
        <IconButton
          className={classes.runDetailsTableMarker}
          sx={iconButtonSx}
        >
          {row?.markerType === 'triangle' && <ArrowDropUp sx={{ fontSize: isMobile ? 20 : 50, color: row?.color }} />}
          {row?.markerType === 'circle' && <Circle sx={{ fontSize: isMobile ? 10 : 15, color: row?.color }} />}
          {row?.markerType === 'square' && <Square sx={{ fontSize: isMobile ? 10 : 15, color: row?.color }} />}
          {row?.markerType === 'cross' && <Close sx={{ fontSize: isMobile ? 15 : 25, color: row?.color }} />}
        </IconButton>
      </Box>
    );
  };

  useEffect(() => {
    const filteredData = hiddenChartData.filter(item => {
      const row = tableRow.find(row => row.index === item.index);
      return row?.hide;
    });
    const isAllFalseForAnyParam = PARAMETER_ANALYSIS_FILTER_PARAMS.some(param => {
      return filterData.every(item => item.isChecked[param] === false);
    });
    if (isAllFalseForAnyParam) {
      setChartData([]);
      return;
    }
    setChartData(filteredData);
  }, [tableRow]);

  const updateHideRunTable = (key, hideValue) => {
    setTableRows(prevItems =>
      prevItems.map(item =>
        item.index === key ? { ...item, hide: !hideValue } : item
      )
    );
  };

  const renderRunHideCell = (params) => {
    return (
      <Box
        className={classes.checkBoxCell}
        sx={{
        }}
      >
        <Checkbox
          size="small"
          checked={params?.row?.hide}
          onClick={() => {
            updateHideRunTable(params?.row?.index, params?.row?.hide);
          }}
        />
      </Box>
    );
  };

  const MobileFilterComponent = () => {
    const getParameterHeader = (parameter) => {
      switch (parameter) {
        case 'flow_in':
          return `Flow Rate (${parametricUnits?.inputs?.flow_in?.label || ''})`;

        case 'rpm':
          return `RPM (${parametricUnits?.inputs?.rpm?.label || ''})`;

        case 'rop':
          return `ROP (${parametricUnits?.inputs?.rop?.label || ''})`;

        default:
          return parameter.toUpperCase();
      }
    };

    const renderRpmMarker = (icon) => (
      <Box
        sx={{
          position: 'relative',
          width: 40,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            borderTop: '2px solid grey',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            bgcolor: COLORS.WHITE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    );

    const renderIndicator = (parameter, row) => {
      switch (parameter) {
        case 'flow_in':
          return (
            <Box
              sx={{
                width: 40,
                borderTop: `3px solid ${
                  FILTER_STYLE_PARAMETRIC_ANALYSIS[row.label]?.color
                }`,
              }}
            />
          );

        case 'rpm':
          if (row.label === 'Value#1') {
            return renderRpmMarker(
              <Circle sx={{ color: COLORS.GRAY, fontSize: 12 }} />
            );
          }

          if (row.label === 'Value#2') {
            return renderRpmMarker(
              <Square sx={{ color: COLORS.GRAY, fontSize: 12 }} />
            );
          }

          if (row.label === 'Value#3') {
            return renderRpmMarker(
              <ArrowDropUp sx={{ color: COLORS.GRAY, fontSize: 22 }} />
            );
          }

          if (row.label === 'Value#4') {
            return renderRpmMarker(
              <Close sx={{ color: COLORS.GRAY, fontSize: 16 }} />
            );
          }

          return null;

        case 'rop':
          return (
            <Box
              sx={{
                width: 40,
                borderTop:
                  row.label === 'Value#4'
                    ? '2px dashed grey'
                    : `2px ${
                        FILTER_STYLE_PARAMETRIC_ANALYSIS[row.label]
                          ?.lineDashStyle || 'solid'
                      } grey`,
              }}
            />
          );

        default:
          return null;
      }
    };

    return (
      <Grid item xs={12} sx={{ position: 'relative', height: '100%', right: 5 }}>
        {parametricAnalysisChartData.inputs.map((input) => (
          <Box
            key={input.parameter}
            sx={{
              width: '90vw',
              maxWidth: 'unset',
              mx: 'auto',
              mb: 3,
              border: '1px solid #d9d9d9',
              backgroundColor: '#f5f5f5',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                py: 1,
                borderBottom: '1px solid #d9d9d9',
                backgroundColor: '#efefef',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  textAlign: 'center',
                }}
              >
                {getParameterHeader(input.parameter)}
              </Typography>
            </Box>

            {/* Rows */}
            {filterData
              .filter(row => row[input.parameter] !== undefined)
              .map((row, index) => (
                <Grid
                  key={`${row._id}_${input.parameter}`}
                  container
                  alignItems="center"
                  sx={{
                    minHeight: 46,
                    backgroundColor: COLORS.WHITE,
                    borderBottom:
                      index === filterData.length - 1
                        ? 'none'
                        : '1px solid #e6e6e6',
                  }}
                >
                  {/* Checkbox */}
                  <Grid
                    item
                    xs={2}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      borderRight: '1px solid #e6e6e6',
                    }}
                  >
                    <Checkbox
                      checked={row.isChecked[input.parameter]}
                      onChange={() =>
                        handleCheckboxChange(
                          row._id,
                          input.parameter
                        )
                      }
                    />
                  </Grid>
                  {/* Value */}
                  <Grid
                    item
                    xs={5}
                    sx={{
                      textAlign: 'center',
                      borderRight: '1px solid #e6e6e6',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      py: 1,
                    }}
                  >
                    {Number(row[input.parameter]).toFixed(
                      parametricUnits?.inputs?.[input.parameter]?.decimals
                    )}
                  </Grid>

                  {/* Indicator */}
                  <Grid
                    item
                    xs={5}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {renderIndicator(input.parameter, row)}
                  </Grid>
                </Grid>
              ))}
          </Box>
        ))}
        <Box
          sx={{
            width: '90vw',
            mx: 'auto',
            mb: 2,
            border: '1px solid #d9d9d9',
            backgroundColor: '#efefef',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 1,
            cursor: 'pointer',
          }}
          onClick={handleResetAll}
        >
          <RestoreOutlined sx={{ mr: 1 }} />
          <Typography fontWeight={700} fontSize={'1rem'}>
            Reset
          </Typography>
        </Box>

        <IconButton
          onClick={() => setShowFilter(false)}
          sx={{
            position: 'absolute',
            left: '95%',
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: '#f1efdf',
            '&:hover': {
              bgcolor: '#e8e6d4',
            },
          }}
        >
          <ChevronLeft />
        </IconButton>
      </Grid>
    );
  };

  const handleScrollToTop = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      '#back-to-top',
    );

    if (anchor) {
      anchor.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  };

  const MobileChartComponent = () => {
    return (
      <>
        {showMobileChartTable
          ? (
            <Drawer
              open={showMobileChartTable}
              onClose={() => setShowMobileChartTable(false)}
              sx={{
                height: '100vh',
              }}
              PaperProps={{ style: { position: 'absolute', width: '100%' } }}
              variant='temporary'
            >
              <Box id='back-to-top' />
              <Grid sx={{ position: 'sticky', top: 0, background: COLORS.WHITE, zIndex: 1000 }} container justifyContent='space-between' alignItems='center'>
                <Box sx={{ p: 1 }}>
                  <FormControl
                  fullWidth
                  sx={{
                    bgcolor: 'transparent',
                    width: { xs: '140px', md: '200px' },
                    height: { xs: '36px', sm: '40px', md: '48px' },
                  }}
                  >
                    <InputLabel
                    id="filter-input-label"
                    sx={{
                      fontSize: { xs: '12px', sm: '14px', md: '16px' },
                    }}
                    >Type</InputLabel>
                    <Select
                      labelId="filter-select-id"
                      id="filter-id"
                      value={plotFor}
                      label="Filter"
                      onChange={(e) => {
                        handleChartAxisReset('resetAll');
                        setDefaultXAxis({ min: '', max: '' });
                        setDefaultYAxis({ min: '', max: '' });
                        setPlotFor(e.target.value);
                      }}
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { xs: '12px', sm: '14px', md: '18px' },
                        py: { xs: 0.2, sm: 0.5, md: 1 },
                        height: { xs: '32px', sm: '40px', md: '48px' },
                        background: 'unset',
                      }}
                    >
                      {['ESD', 'ECD'].map((item, idItem) => (
                        <MenuItem
                          key={`item_${item}_${idItem}`}
                          value={item}
                           sx={{
                             fontSize: { xs: '12px', sm: '14px', md: '18px' },
                             fontWeight: 'bold',
                             color: item?.color,
                           }}
                        >
                          {item === 'ESD' ? 'ESD (Connection)' : 'ECD (Drilling)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box display={'flex'} justifyContent='flex-end' alignItems='center'>
                  <IconButton
                    sx={{
                      ml: 1
                    }}
                    onClick={() => {
                      setShowMobileChartTable(!showTable);
                    }}>
                    <CancelPresentationRounded fontSize="large" />
                  </IconButton>
                </Box>
              </Grid>
              <Grid item xs={12}>
                {tableRow.map((row, index) => (
                  <Box key={index} className={classes.gridContainer}>
                    {columns(plotFor, parametricUnits, renderRunCell, renderRunHideCell, customColumn)?.map((col, idx) => {
                      if (!col.field || col.field === 'hide') return null;
                      if (col.field === 'sbp' && plotFor === 'ESD') return null;
                      if (col.field === 'connection_sbp' && plotFor === 'ECD') return null;
                      if (col.field === 'runNumber') {
                        return (
                          <Grid container key={idx} className={classes.gridItem}>
                            <Typography className={classes.headerName} variant="body1">
                              {col.headerName}
                            </Typography>
                            <IconButton
                              sx={{ flex: 0.5, p: 0, m: 0 }}
                              variant="body1"
                              onClick={() => {
                                if (col.field === 'runNumber' && row?.depth_points?.length) {
                                  setShowRunChart({
                                    open: true,
                                    data: {
                                      row
                                    }
                                  });
                                }
                              }}
                            >{renderRunCell({
                              row,
                              field: col.field,
                              value: row[col.field],
                            })}</IconButton>
                          </Grid>
                        );
                      }
                      const value = col?.valueGetter
                        ? col.valueGetter({ row })
                        : col?.valueFormatter
                          ? col.valueFormatter({
                            row,
                            field: col.field,
                            value: row[col.field],
                          })
                          : row[col.field];
                      return (
                        <Grid container key={idx} className={classes.gridItem}>
                          <Typography className={classes.headerName} variant="body1">
                            {col.headerName}
                          </Typography>
                          <Typography sx={{ flex: 0.5 }} variant="body1">{value}</Typography>
                        </Grid>
                      );
                    })}
                  </Box>
                ))}
              </Grid>
              <Fab size='small' className={classes.accordianScrollUp} onClick={handleScrollToTop}>
                <KeyboardArrowUp sx={{ fontSize: '30px' }} />
              </Fab>
            </Drawer>
            )
          : (
            <>
              <Grid item xs={12} sx={{ pb: isMobile ? 3 : 0 }}>
                <Box
                  sx={{
                    position: 'relative'
                  }}
                >
                  {/* scroll box on the left, so chart has a way to scroll out on mobile */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: 55,
                      height: '100%',
                      touchAction: 'auto',
                      zIndex: 10,
                      background: 'transparent',
                    }}
                  />
                  <Box
                    sx={{
                      touchAction: 'none'
                    }}
                  >
                    <CanvasJSChart options={options} />
                  </Box>
                </Box>

                <Box style={{ position: 'relative' }}>
                  <Box className={classes.xAxisEditBtn}>
                    <Box className={classes.editBox}>
                      <ChartsAxisPopover
                        title='X Axis'
                        defaultMin={getValidValue(chartXAxis?.min, defaultXAxis?.lowest - 0.1)}
                        defaultMax={getValidValue(chartXAxis?.max, defaultXAxis?.highest + 0.1)}
                        handleReset
                        onReset={() => handleChartAxisReset('x')}
                        onSubmit={(min, max) => {
                          setChartXAxis({ min: Number(min), max: Number(max) });
                        }} />
                    </Box>
                  </Box>

                  <Box className={classes.yAxisEditBtn}>
                    <Box className={classes.editBox}>
                      <ChartsAxisPopover
                        title='Y Axis'
                        defaultMin={getValidValue(chartYAxis?.min, defaultYAxis.min)}
                        defaultMax={getValidValue(chartYAxis?.max, defaultYAxis.max)}
                        onReset={() => handleChartAxisReset('y')}
                        onSubmit={(min, max) => {
                          setChartYAxis({ min: Number(min), max: Number(max) });
                        }} />
                    </Box>
                  </Box>
                </Box>
                {!showMobileChartTable && <Box className={classes.menuTogglerBtn}>
                  <IconButton
                    onClick={() => {
                      setShowMobileChartTable(true);
                    }}>
                    <ChevronLeft sx={{ fontSize: '33px', display: 'none' }} />
                  </IconButton>
                </Box>}
              </Grid>
              <Box sx={{ touchAction: 'auto' }}>
                <Box className={classes.scrollZone}>
                </Box>
              </Box>
            </>
            )}
        {
          showRunChart.open
            ? <RunDetailsChartDialog
              data={showRunChart.data}
              isOpen={showRunChart.open}
              onCloseClick={() => {
                setShowRunChart({
                  open: false,
                  data: null
                });
              }}
              hydraulicsLanding={hydraulicsLanding}
              units={units}
              featuredWell={parametricAnalysisChartData}
              window={window}
            />
            : null
        }
      </>
    );
  };

  return (
    <React.Fragment>
        <Box display="flex" justifyContent="center" margin="1%" alignItems="center">
            <Grid item>
              <Box
                className={classes.runDetailsFilter}
                onClick={() => setShowFilter(!showFilter)}
              >
                {!showFilter && (<>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Filter</Typography>
                  <IconButton variant="solid" fontSize="large">
                    <FilterAltRounded />
                  </IconButton></>
                )}
              </Box>
            </Grid>
          {/* Filter Table */}
        {showFilter && (
          <>
            {isMobile
              ? (
              <MobileFilterComponent />
                )
              : (
              <Grid item sx={{
                position: 'relative',
                flexBasis: `${filterTableWidth[columns_table.length - 2]}%`,
                flexGrow: 1,
                minWidth: 0,
                maxWidth: '80%',
              }}>
                {/* Datagrid inside a Box to allow absolute positioning */}
                <Box sx={{ position: 'relative' }}>
                  <Datagrid
                    toolbar={{
                      options: {
                        columns: false,
                        filters: false,
                        density: false,
                        export: {
                          enabled: false,
                        }
                      },
                    }}
                    sx={{
                      '.MuiDataGrid-cell': { fontSize: '1rem', padding: '2px' },
                      '.MuiDataGrid-columnHeader': { fontSize: '1rem', fontWeight: '900 !important' },
                      '& .MuiDataGrid-footerContainer': { display: 'none' },
                      '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                        outline: 'none !important',
                      },
                      '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus':
                      {
                        outline: 'none !important',
                      },
                    }}
                    data={filterData}
                    columns={columns_table}
                    height={filterData?.length <= 3 ? '225px' : '264px'}
                    autoHeight={filterData?.length <= 3}
                    disableRowSelectionOnClick
                    hideFooterPagination
                    hideFooterSelectedRowCount
                    getRowHeight={() => 'auto'}
                    pageSize={100}
                  />

                  {/* Chevron Left Icon Positioned Over Datagrid on the Right */}
                  <IconButton
                    onClick={() => setShowFilter(false)}
                    className={classes.filterTogglerTableBtn}
                  >
                    <ChevronLeft sx={{ fontSize: '30px' }} />
                  </IconButton>
                </Box>
              </Grid>
                )}
          </>
        )}
        </Box>
        <Grid container item xs={showTable ? 8 : 12} marginTop={showFilter ? (filterData.length < 3 ? '-1.25%' : '1.5%') : '0%'} alignItems="center" justifyContent="center">
          <Grid item>
            <CustomLegend />
          </Grid>
        </Grid>
        {isMobile
          ? (
          <MobileChartComponent />
            )
          : (
        <Box style={{
          overflow: 'auto',
        }}>
          <Grid container>
            <Grid item xs={showTable ? 7 : 12} sx={{
              p: 2,
              pt: 4,
            }}>
                  <CanvasJSChart
                    options={options}
                    key={JSON.stringify(chartData)}
                  />
                    <Box style={{ position: 'relative' }}>
                      <Box className={classes.xAxisEditBtn}>
                        <Box className={classes.editBox}>
                          <ChartsAxisPopover
                            title='X Axis'
                            defaultMin={chartXAxis?.min ? chartXAxis?.min : defaultXAxis?.lowest - 0.1}
                            defaultMax={chartXAxis?.max ? chartXAxis?.max : defaultXAxis?.highest + 0.1}
                            handleReset
                            onReset={() => handleChartAxisReset('x')}
                            onSubmit={(min, max) => {
                              setChartXAxis({ min: Number(min), max: Number(max) });
                            }} />
                        </Box>
                      </Box>

                      <Box className={classes.yAxisEditBtn}>
                        <Box className={classes.editBox}>
                          <ChartsAxisPopover
                            title='Y Axis'
                            defaultMin={chartYAxis?.min ? chartYAxis?.min : defaultYAxis.min}
                            defaultMax={chartYAxis?.max ? chartYAxis?.max : defaultYAxis.max}
                            onReset={() => handleChartAxisReset('y')}
                            onSubmit={(min, max) => {
                              setChartYAxis({ min: Number(min), max: Number(max) });
                            }} />
                        </Box>
                      </Box>
                    </Box>
            </Grid>
            {!showTable && <Box className={classes.menuTogglerBtn}>
              <IconButton
                onClick={() => {
                  setShowTable(!showTable);
                }}>
                <ChevronLeft sx={{ fontSize: '33px' }} />
              </IconButton>
            </Box>}
            {showTable && <Grid item xs={5} marginTop={'1.5%'}>
              <Box className={classes.menuTogglerTableBtn}>
                <IconButton
                  onClick={() => {
                    setShowTable(!showTable);
                  }}>
                  <ChevronRight sx={{ fontSize: '33px' }} />
                </IconButton>
              </Box>
              <Box display={'flex'} justifyContent={'start'} marginLeft={'2.25%'}>
              <Grid item xs={3}>
                <FormControl fullWidth sx={{ bgcolor: 'transparent', width: '200px' }}>
                  <InputLabel id="filter-input-label">Type</InputLabel>
                  <Select
                    labelId="filter-select-id"
                    id="filter-id"
                    value={plotFor}
                    label="Filter"
                    onChange={(e) => {
                      handleChartAxisReset('resetAll');
                      setDefaultXAxis({ min: '', max: '' });
                      setDefaultYAxis({ min: '', max: '' });
                      setPlotFor(e.target.value);
                    }}
                    sx={{
                      fontWeight: 'bolder',
                      fontSize: '18px',
                      background: 'unset'
                    }}
                  >
                    {['ESD', 'ECD'].map((item, idItem) => (
                      <MenuItem
                        key={`item_${item}_${idItem}`}
                        value={item}
                        sx={{ color: item?.color, fontSize: '18px', fontWeight: 'bold' }}
                      >
                        {item === 'ESD' ? 'ESD (Connection)' : 'ECD (Drilling)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                </Grid>
              </Box>
              {showTable && <Grid item xs={12}>
                <Datagrid
                  data={tableRow}
                  sx={{
                    '.MuiDataGrid-cell': {
                      fontSize: '1rem',
                      padding: '2px',
                    },
                    '.MuiDataGrid-columnHeader': {
                      fontSize: '1.2rem',
                      fontWeight: '900 !important',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      overflow: 'visible',
                      lineHeight: '1.43rem',
                      whiteSpace: 'normal',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      minHeight: '0px'
                    },
                    '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                      outline: 'none !important',
                    },
                    '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus':
                    {
                      outline: 'none !important',
                    },
                  }}
                  columns={columns(plotFor, parametricUnits, renderRunCell, renderRunHideCell, customColumn)}
                  onCellClick={(row) => {
                    if (row.field === 'runNumber' && row?.row?.depth_points?.length) {
                      setShowRunChart({
                        open: true,
                        data: row
                      });
                    }
                  }}
                  disableRowSelectionOnClick
                  disableStickyHeader={true}
                  autoHeight={false}
                  {...(tableRow.length <= 100
                    ? { hideFooterPagination: true, hideFooterSelectedRowCount: true }
                    : {})}
                  height={'800px'}
                  getRowHeight={() => 'auto'}
                  pageSize={100}
                  toolbar={{
                    options: {
                      columns: false,
                      filters: false,
                      density: false,
                      export: {
                        enabled: false,
                      }
                    },
                  }}
                />
              </Grid>}
            </Grid>}
          </Grid>
        </Box>
            )}
      {
        showRunChart.open
          ? <RunDetailsChartDialog
            data={showRunChart.data}
            isOpen={showRunChart.open}
            onCloseClick={() => {
              setShowRunChart({
                open: false,
                data: null
              });
            }}
            hydraulicsLanding={hydraulicsLanding}
            units={units}
            featuredWell={parametricAnalysisChartData}
            window={window}
          />
          : null
      }
    </React.Fragment>
  );
};
ParametricAnalysisChart.propTypes = {
  data: PropTypes.any,
  units: PropTypes.object,
  window: PropTypes.object,
  hydraulicsLanding: PropTypes.object
};

export default ParametricAnalysisChart;
